// Informes (Módulo E) sobre TanStack Query. Mismo patrón que
// `hooks/useUsuarios.ts`: la cache de la query es la fuente de verdad de la
// lista y las mutaciones invalidan esa key.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { registroDeTerrenoKey } from "@/hooks/useRegistroDeTerreno";

import {
  INTERVALO_DE_POLL_DEL_CONTEXTO_MS,
  aceptarCotizacionRequest,
  actualizarInformeRequest,
  adjuntarArchivoDeAntecedenteRequest,
  clasificarArchivoDeAntecedenteRequest,
  agregarFilaRequest,
  anotarElArchivoRequest,
  corregirFechaDelArchivoRequest,
  corregirFilaRequest,
  crearCotizacionRequest,
  crearInformeRequest,
  debePollearElContexto,
  enviarATerrenoRequest,
  fetchCatalogosDelInforme,
  fetchGuiaDeTerreno,
  fetchInforme,
  fetchInformesEnProceso,
  guardarDescripcionRequest,
  guardarAguasRequest,
  guardarSuelosRequest,
  guardarDocumentosRequest,
  guardarLaPestanaRequest,
  quitarArchivoDeAntecedenteRequest,
  quitarFotoDeLaFilaRequest,
  quitarFotografiaRequest,
  rotularLaFotoDeLaFilaRequest,
  rotularLaFotografiaRequest,
  quitarImagenDelExpedienteRequest,
  quitarImagenDelInformeRequest,
  quitarImagenDelRolRequest,
  mantenerFilaRequest,
  quitarFilaRequest,
  registrarPagoDelAbonoRequest,
  reprocesarArchivoDeAntecedenteRequest,
  retirarDeTerrenoRequest,
  revertirPagoDelAbonoRequest,
  subirFotoDeLaFilaRequest,
  subirFotografiaRequest,
  subirImagenDelExpedienteRequest,
  subirImagenDelInformeRequest,
  subirImagenDelRolRequest,
  type ActualizarInformeInput,
  type CamposDeLaFila,
  type CategoriaDocumento,
  type CrearCotizacionInput,
  type CrearInformeInput,
  type FiltrosDeInformes,
  type Informe,
  type GuardarDescripcionInput,
  type GuardarLaPestanaInput,
  type PestanaConCuadro,
  type GuardarAguasInput,
  type GuardarSuelosInput,
  type Obligatoriedad,
  type TipoDeFotografiaDelInforme,
  type TipoDeImagenDelInforme,
} from "@/lib/informes";

export const INFORMES_KEY = ["informes"] as const;
export const CATALOGOS_INFORME_KEY = ["informes", "catalogos"] as const;

/** Key de la ficha. Anidada bajo INFORMES_KEY para poder invalidar todo junto. */
export const informeKey = (id: number) => ["informes", "ficha", id] as const;

/**
 * Catálogos del formulario (contenidos, documentos y usuarios asignables).
 *
 * `staleTime` alto a propósito: son datos maestros que cambian con el
 * mantenedor, no durante el llenado de un formulario. Sin él, cada vez que la
 * ventana recupera el foco se dispara un refetch que puede reordenar los
 * select mientras el usuario está escribiendo.
 */
export function useCatalogosDelInforme() {
  return useQuery({
    queryKey: CATALOGOS_INFORME_KEY,
    queryFn: fetchCatalogosDelInforme,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Listado de Informes en proceso (pantalla `16 UN`).
 *
 * Los filtros van DENTRO de la queryKey: cada combinación es una respuesta
 * distinta del servidor —filtra y pagina el backend, no el front— y con una
 * key única la cache las guarda por separado, así volver a un filtro ya
 * visitado no vuelve a pedirlo.
 *
 * `placeholderData` conserva la página anterior mientras llega la nueva: sin
 * él, cambiar de filtro o de página vacía la tabla y la pantalla salta a los
 * esqueletos en cada clic.
 */
export function useInformesEnProceso(filtros: FiltrosDeInformes = {}) {
  return useQuery({
    queryKey: [...INFORMES_KEY, filtros],
    queryFn: () => fetchInformesEnProceso(filtros),
    placeholderData: (anterior) => anterior,
  });
}

export function useInforme(id: number) {
  return useQuery({
    queryKey: informeKey(id),
    queryFn: () => fetchInforme(id),
    // `enabled` sobre un id > 0: la ruta trae el id como string y un
    // `Number("abc")` da NaN. Sin esta guarda, la pantalla dispararía un GET
    // a `/api/informes/NaN`.
    enabled: Number.isFinite(id) && id > 0,
    // FUNCIÓN, no un número fijo — y es el primer `refetchInterval` de este
    // archivo. Un número fijo sondearía PARA SIEMPRE, incluso con el
    // expediente entero ya procesado; acá solo corresponde mientras algún
    // archivo esté EN VUELO. `debePollearElContexto` es la MISMA pregunta que
    // decide "Reintentar" en la pantalla (`InformeDocumentoArchivo::contextoEnCurso()`
    // del lado del servidor): las filas TRABADAS —de antes de este cambio,
    // que nunca se tocaron— no cuentan como en curso, así que no sondean
    // solas para siempre.
    refetchInterval: (query) =>
      debePollearElContexto(query.state.data) ? INTERVALO_DE_POLL_DEL_CONTEXTO_MS : false,
  });
}

export function useCrearInforme() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CrearInformeInput) => crearInformeRequest(input),
    // Invalidar y no sembrar: el 201 devuelve el informe con sus relaciones,
    // pero no el listado ordenado completo, así que un refetch es más simple
    // y correcto (mismo criterio que `useCrearUsuario`).
    onSuccess: () => queryClient.invalidateQueries({ queryKey: INFORMES_KEY }),
  });
}

/**
 * Las dos mutaciones de edición SIEMBRAN la ficha con la respuesta en vez de
 * invalidarla: el backend devuelve el informe completo y ya cargado, así que
 * un refetch sería una segunda vuelta para traer lo mismo — y en el medio la
 * pantalla mostraría los valores viejos.
 *
 * El listado sí se invalida: su orden y sus columnas no salen de esta
 * respuesta.
 */
export function useActualizarInforme(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ActualizarInformeInput) => actualizarInformeRequest(id, input),
    onSuccess: (informe) => {
      queryClient.setQueryData(informeKey(id), informe);
      queryClient.invalidateQueries({ queryKey: INFORMES_KEY });
    },
  });
}

export function useGuardarDocumentos(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      categoria,
      documentos,
      notas,
    }: {
      categoria: CategoriaDocumento;
      documentos: { documento_tipo_id: number; obligatoriedad: Obligatoriedad }[];
      notas: string | null;
    }) => guardarDocumentosRequest(id, categoria, documentos, notas),
    onSuccess: (informe) => {
      queryClient.setQueryData(informeKey(id), informe);
      queryClient.invalidateQueries({ queryKey: INFORMES_KEY });
    },
  });
}

/**
 * Sube (o reemplaza) la imagen satelital ÚNICA del informe.
 *
 * SIEMBRA la ficha en vez de invalidarla, mismo criterio que
 * `useActualizarInforme`: el backend devuelve el informe completo con la
 * imagen ya puesta, así que un refetch sería una segunda vuelta para traer lo
 * mismo — y en el medio la pantalla mostraría la imagen anterior, que es justo
 * lo que se acaba de cambiar.
 *
 * El listado NO se invalida: la imagen no sale en ninguna de sus columnas.
 */
export function useSubirImagenDelInforme(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (archivo: File) => subirImagenDelInformeRequest(informeId, archivo),
    onSuccess: (informe) => queryClient.setQueryData(informeKey(informeId), informe),
  });
}

export function useQuitarImagenDelInforme(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => quitarImagenDelInformeRequest(informeId),
    onSuccess: (informe) => queryClient.setQueryData(informeKey(informeId), informe),
  });
}

/* --------------------------------------------------------------------------
   Pestaña "Descripción de la propiedad"
   -------------------------------------------------------------------------- */

/**
 * Guarda el TEXTO de la pestaña: los deslindes de cada rol, sus casillas y las
 * notas al pie. Las imágenes tienen sus propios hooks, abajo.
 *
 * SIEMBRA la ficha, como el resto de las escrituras del informe: el backend
 * devuelve el informe completo, y un refetch sería una segunda vuelta para
 * traer lo mismo mientras la pantalla muestra lo viejo.
 */
export function useGuardarDescripcion(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: GuardarDescripcionInput) => guardarDescripcionRequest(informeId, input),
    onSuccess: (informe) => queryClient.setQueryData(informeKey(informeId), informe),
  });
}

/**
 * La captura del límite de un rol.
 *
 * EL ROL VIAJA EN LA MUTACIÓN y no en el hook, por lo mismo que el tipo de
 * documento en `useAdjuntarArchivoDeAntecedente`: un hook por rol habría que
 * llamarlo dentro del `map` que dibuja la lista, y la cantidad de hooks
 * cambiaría al agregar un rol en la otra pestaña.
 */
export function useSubirImagenDelRol(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ rolId, archivo }: { rolId: number; archivo: File }) =>
      subirImagenDelRolRequest(informeId, rolId, archivo),
    onSuccess: (informe) => queryClient.setQueryData(informeKey(informeId), informe),
  });
}

export function useQuitarImagenDelRol(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rolId: number) => quitarImagenDelRolRequest(informeId, rolId),
    onSuccess: (informe) => queryClient.setQueryData(informeKey(informeId), informe),
  });
}

/** Uno de los tres encuadres fijos. El tipo viaja en la mutación, como el rol. */
export function useSubirImagenDelExpediente(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tipo, archivo }: { tipo: TipoDeImagenDelInforme; archivo: File }) =>
      subirImagenDelExpedienteRequest(informeId, tipo, archivo),
    onSuccess: (informe) => queryClient.setQueryData(informeKey(informeId), informe),
  });
}

export function useQuitarImagenDelExpediente(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tipo: TipoDeImagenDelInforme) =>
      quitarImagenDelExpedienteRequest(informeId, tipo),
    onSuccess: (informe) => queryClient.setQueryData(informeKey(informeId), informe),
  });
}

/* --------------------------------------------------------------------------
   Pestaña "Descripción de suelos"
   -------------------------------------------------------------------------- */

/** El guardado de la pestaña: su prosa y su cuadro de clases de uso. */
export function useGuardarSuelos(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: GuardarSuelosInput) => guardarSuelosRequest(informeId, input),
    onSuccess: (informe) => queryClient.setQueryData(informeKey(informeId), informe),
  });
}

/**
 * El "Guardar cambios" de la pestaña "Descriptor de aguas".
 *
 * UNA SOLA MUTACIÓN PARA TODA LA PESTAÑA, al revés que las cuatro con cuadro y
 * fotos, que necesitan seis. Acá los dos cuadros son texto y viajan con la
 * casilla y las notas en el mismo cuerpo: no hay ningún endpoint por fila
 * porque de una fuente de agua no cuelga ningún objeto del bucket.
 */
export function useGuardarAguas(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: GuardarAguasInput) => guardarAguasRequest(informeId, input),
    onSuccess: (informe) => {
      queryClient.setQueryData(informeKey(informeId), informe);

      // Enlazar una fila a un registro de terreno cambia dónde cuelga ese
      // registro: la fila nueva lo recibe y la fuente suelta deja de listarse.
      // Sin releerlo, la pantalla mostraría las dos hasta el próximo foco.
      void queryClient.invalidateQueries({ queryKey: registroDeTerrenoKey(informeId) });
    },
  });
}

/**
 * AGREGA una fotografía a un encuadre del expediente.
 *
 * EL ENCUADRE VIAJA EN LA MUTACIÓN, por lo mismo que el tipo en
 * `useSubirImagenDelExpediente`: un hook por encuadre habría que llamarlo
 * dentro del `map` que dibuja los bloques.
 */
export function useSubirFotografia(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tipo, archivo }: { tipo: TipoDeFotografiaDelInforme; archivo: File }) =>
      subirFotografiaRequest(informeId, tipo, archivo),
    onSuccess: (informe) => queryClient.setQueryData(informeKey(informeId), informe),
  });
}

/** Quita UNA fotografía, por su id. */
export function useQuitarFotografia(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fotografiaId: number) => quitarFotografiaRequest(informeId, fotografiaId),
    onSuccess: (informe) => queryClient.setQueryData(informeKey(informeId), informe),
  });
}

/** Escribe la leyenda de UNA fotografía. Se guarda sola, no con la pestaña. */
export function useRotularFotografia(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fotografiaId, leyenda }: { fotografiaId: number; leyenda: string | null }) =>
      rotularLaFotografiaRequest(informeId, fotografiaId, leyenda),
    onSuccess: (informe) => queryClient.setQueryData(informeKey(informeId), informe),
  });
}

/* --------------------------------------------------------------------------
   Las tres pestañas con CUADRO: maquinarias, instalaciones, construcciones
   -------------------------------------------------------------------------- */

/**
 * Toda escritura del expediente SIEMBRA la ficha en vez de refrescarla.
 *
 * El backend devuelve el informe completo, así que un refetch sería una
 * segunda vuelta para traer lo mismo mientras la pantalla muestra lo viejo.
 * Estaba escrito once veces —una por endpoint— y siempre igual.
 */
function useMutacionQueSiembraLaFicha<T>(
  informeId: number,
  enviar: (valor: T) => Promise<Informe>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: enviar,
    onSuccess: (informe: Informe) => queryClient.setQueryData(informeKey(informeId), informe),
  });
}

/**
 * Guarda lo que es de la PESTAÑA: la descripción general, su casilla, la
 * casilla "no cuenta con…" y las notas al pie.
 *
 * Las FILAS tienen sus propios hooks, abajo, porque cada una se guarda con su
 * propio botón — el de acá no las toca.
 */
export function useGuardarLaPestana(cuadro: PestanaConCuadro, informeId: number) {
  return useMutacionQueSiembraLaFicha(informeId, (input: GuardarLaPestanaInput) =>
    guardarLaPestanaRequest(cuadro, informeId, input),
  );
}

export function useAgregarFila(cuadro: PestanaConCuadro, informeId: number) {
  return useMutacionQueSiembraLaFicha(informeId, (campos: CamposDeLaFila) =>
    agregarFilaRequest(cuadro, informeId, campos),
  );
}

/**
 * Corrige una fila del cuadro.
 *
 * LA FILA VIAJA EN LA MUTACIÓN y no en el hook, por lo mismo que el rol en
 * `useSubirImagenDelRol`: un hook por fila habría que llamarlo dentro del
 * `map` que dibuja el cuadro, y la cantidad de hooks cambiaría al agregar una.
 */
export function useCorregirFila(cuadro: PestanaConCuadro, informeId: number) {
  return useMutacionQueSiembraLaFicha(
    informeId,
    ({ filaId, campos }: { filaId: number; campos: CamposDeLaFila }) =>
      corregirFilaRequest(cuadro, informeId, filaId, campos),
  );
}

export function useMantenerFila(cuadro: PestanaConCuadro, informeId: number) {
  return useMutacionQueSiembraLaFicha(informeId, (filaId: number) =>
    mantenerFilaRequest(cuadro, informeId, filaId),
  );
}

export function useQuitarFila(cuadro: PestanaConCuadro, informeId: number) {
  return useMutacionQueSiembraLaFicha(informeId, (filaId: number) =>
    quitarFilaRequest(cuadro, informeId, filaId),
  );
}

export function useSubirFotoDeLaFila(cuadro: PestanaConCuadro, informeId: number) {
  return useMutacionQueSiembraLaFicha(
    informeId,
    ({ filaId, archivo }: { filaId: number; archivo: File }) =>
      subirFotoDeLaFilaRequest(cuadro, informeId, filaId, archivo),
  );
}

export function useRotularFotoDeLaFila(cuadro: PestanaConCuadro, informeId: number) {
  return useMutacionQueSiembraLaFicha(
    informeId,
    ({ filaId, fotoId, leyenda }: { filaId: number; fotoId: number; leyenda: string | null }) =>
      rotularLaFotoDeLaFilaRequest(cuadro, informeId, filaId, fotoId, leyenda),
  );
}

export function useQuitarFotoDeLaFila(cuadro: PestanaConCuadro, informeId: number) {
  return useMutacionQueSiembraLaFicha(
    informeId,
    ({ filaId, fotoId }: { filaId: number; fotoId: number }) =>
      quitarFotoDeLaFilaRequest(cuadro, informeId, filaId, fotoId),
  );
}

/**
 * Adjunta un archivo recibido a un tipo de documento del informe.
 *
 * Atado al INFORME y con el tipo de documento en la mutación, por lo mismo que
 * el hook de la imagen: uno por documento habría que llamarlo dentro del `map`
 * que dibuja la lista, y la cantidad de hooks cambiaría al dar de alta un
 * documento en el catálogo.
 *
 * SIEMBRA la ficha: el backend devuelve el informe completo con sus archivos, y
 * un refetch sería una segunda vuelta para traer lo mismo mientras la pantalla
 * muestra la lista vieja — sin el archivo que se acaba de adjuntar.
 *
 * El listado NO se invalida: los adjuntos no salen en ninguna de sus columnas.
 */
export function useAdjuntarArchivoDeAntecedente(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ documentoTipoId, archivo }: { documentoTipoId: number; archivo: File }) =>
      adjuntarArchivoDeAntecedenteRequest(informeId, documentoTipoId, archivo),
    onSuccess: (informe) => queryClient.setQueryData(informeKey(informeId), informe),
  });
}

export function useQuitarArchivoDeAntecedente(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (archivoId: number) => quitarArchivoDeAntecedenteRequest(informeId, archivoId),
    onSuccess: (informe) => queryClient.setQueryData(informeKey(informeId), informe),
  });
}

/**
 * "Reintentar" un archivo trabado o fallido (`odd/tasks/transcripcion-en-limbo.md`).
 *
 * SIEMBRA la ficha, mismo criterio que sus dos vecinas: el servidor devuelve
 * el informe completo con el archivo ya reiniciado —`contexto_en_curso` en
 * `true`—, y de ahí en más es el sondeo de `useInforme` el que trae el
 * resultado sin que nadie tenga que refrescar a mano.
 */
export function useReprocesarArchivoDeAntecedente(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (archivoId: number) => reprocesarArchivoDeAntecedenteRequest(informeId, archivoId),
    onSuccess: (informe) => queryClient.setQueryData(informeKey(informeId), informe),
  });
}

/**
 * Clasifica, reclasifica o devuelve a "sin clasificar" un archivo del
 * expediente.
 *
 * SIEMBRA la ficha, igual que sus vecinas: el servidor responde el informe
 * entero con el archivo ya movido, y un refetch mostraría un instante la lista
 * vieja.
 */
export function useClasificarArchivoDeAntecedente(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      archivoId,
      documentoTipoId,
    }: {
      archivoId: number;
      documentoTipoId: number | null;
    }) => clasificarArchivoDeAntecedenteRequest(informeId, archivoId, documentoTipoId),
    onSuccess: (informe) => queryClient.setQueryData(informeKey(informeId), informe),
  });
}

/**
 * Corrige la fecha de EMISIÓN de un archivo (rebanada 2 de
 * `odd/tasks/fecha-del-documento-y-tasacion-mas-reciente.md`).
 *
 * SIEMBRA la ficha, mismo criterio que sus dos vecinas: el backend devuelve el
 * informe completo con la fecha ya corregida, así que un refetch sería una
 * segunda vuelta para traer lo mismo mientras la pantalla sigue mostrando la
 * fecha vieja.
 */
export function useCorregirFechaDelArchivo(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ archivoId, fecha }: { archivoId: number; fecha: string | null }) =>
      corregirFechaDelArchivoRequest(informeId, archivoId, fecha),
    onSuccess: (informe) => queryClient.setQueryData(informeKey(informeId), informe),
  });
}

/**
 * Escribe la nota del tasador sobre un archivo. Siembra la ficha, igual que
 * {@see useCorregirFechaDelArchivo}.
 */
export function useAnotarElArchivo(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ archivoId, nota }: { archivoId: number; nota: string | null }) =>
      anotarElArchivoRequest(informeId, archivoId, nota),
    onSuccess: (informe) => queryClient.setQueryData(informeKey(informeId), informe),
  });
}

export function useCrearCotizacion(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CrearCotizacionInput) => crearCotizacionRequest(informeId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: informeKey(informeId) });
      queryClient.invalidateQueries({ queryKey: INFORMES_KEY });
    },
  });
}

/**
 * Registrar a mano la aceptación del cliente (entrega C).
 *
 * Invalida la ficha y el listado, y las DOS importan: el informe cambia de
 * estado —lo que cambia el rótulo del botón a "Completar informe"— y la columna
 * de entrega deja de mostrar el plazo para mostrar el día comprometido, que es
 * el cambio que el usuario está esperando ver.
 *
 * Invalida en vez de sembrar, mismo criterio que `useCrearCotizacion`: la
 * respuesta es la COTIZACIÓN, no el informe completo.
 */
export function useAceptarCotizacion(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (motivo: string) => aceptarCotizacionRequest(informeId, motivo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: informeKey(informeId) });
      queryClient.invalidateQueries({ queryKey: INFORMES_KEY });
    },
  });
}

/**
 * Registrar —o corregir— el pago del abono.
 *
 * Invalida el listado, que es donde vive la columna del anticipo, y también la
 * ficha: la cotización embebida en ella trae las mismas columnas, y dejarla
 * vieja haría que abrir el informe mostrara "pendiente" sobre un abono que se
 * acaba de marcar pagado.
 */
export function useRegistrarPagoDelAbono(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fechaPago: string) => registrarPagoDelAbonoRequest(informeId, fechaPago),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: informeKey(informeId) });
      queryClient.invalidateQueries({ queryKey: INFORMES_KEY });
    },
  });
}

/** Deshacer el registro del pago. Invalida lo mismo, por lo mismo. */
export function useRevertirPagoDelAbono(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => revertirPagoDelAbonoRequest(informeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: informeKey(informeId) });
      queryClient.invalidateQueries({ queryKey: INFORMES_KEY });
    },
  });
}

/** La guía de terreno. Se pide solo con la modal abierta: es una consulta más por ficha. */
export function useGuiaDeTerreno(informeId: number, habilitada: boolean) {
  return useQuery({
    queryKey: [...informeKey(informeId), "guia-terreno"],
    queryFn: () => fetchGuiaDeTerreno(informeId),
    enabled: habilitada,
  });
}

/**
 * Enviar el informe a la app de terreno, o retirarlo. Invalidan la ficha —el
 * botón cambia de estado— y la guía, cuyo encabezado dice si está disponible.
 */
export function useEnviarATerreno(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => enviarATerrenoRequest(informeId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: informeKey(informeId) }),
  });
}

export function useRetirarDeTerreno(informeId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => retirarDeTerrenoRequest(informeId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: informeKey(informeId) }),
  });
}
